import { ClientOnly, createFileRoute, isNotFound } from '@tanstack/react-router';
import { useTranslation } from '@/lib/i18n';
import { Paragraph, Title, useAppForm } from '@worldcoo/ui/components/form';
import { Animate } from '@worldcoo/ui/custom/animate';
import { detailsSchema, usePersistedStore, useEphemeralStore } from '@/lib/app-store';
import { useEffect, useRef, useState, type RefObject } from 'react';
import { usePortalCampaignQuery, usePortalAuthorQuery, preregisterDonationOptions } from '@/lib/queries';

import { formatList, cn, formatNumber, savedTaxAmount } from '@worldcoo/ui/lib/utils';
import { BeneficiariesIcon } from '@worldcoo/ui/custom/beneficiaries-icon';
import { sanitizeHTML } from '@worldcoo/ui/lib/sanitize-html';
import { revalidateLogic } from '@tanstack/react-form';
import { TermsAndConditionsDialog } from '../-components/terms-and-conditions';
import { TermsCheckboxField } from './-components/terms-checkbox-field';
import { useMutation } from '@tanstack/react-query';
import { generateCaptchaToken } from '@worldcoo/ui/lib/captcha';
import { applicationType } from '@worldcoo/monolith/shared-context';
import { amountToMinorUnits, localeToLanguage } from '@worldcoo/monolith/core';
import { useServerFn } from '@tanstack/react-start';
import { getPaymentsMethodsFn } from '@/lib/server-functions';
import { toast } from 'sonner';

export const Route = createFileRoute('/$portalId/$campaignId/donate/details')({
	component: Component,
});

function Component() {
	const { portalId, campaignId } = Route.useParams();
	const navigate = Route.useNavigate();
	const { i18n, language } = useTranslation();
	const defaultValues = usePersistedStore((state) => state.details);
	const amount = usePersistedStore((state) => state.amount);
	const hasHydrated = usePersistedStore((state) => state._hasHydrated);
	const setDetails = usePersistedStore((state) => state.setDetails);
	const termsContent = usePersistedStore((state) => state.termsContent);
	const ngosTermsContent = usePersistedStore((state) => state.ngosTermsContent);
	const telefonicaTermsContent = usePersistedStore((state) => state.telefonicaTermsContent);
	const setCachedPreregistration = useEphemeralStore((state) => state.setCachedPreregistration);
	const setCachedPaymentMethods = useEphemeralStore((state) => state.setCachedPaymentMethods);

	const { mutateAsync: preregisterDonation } = useMutation(preregisterDonationOptions());
	const getPaymentMethods = useServerFn(getPaymentsMethodsFn);

	const currencyCode = 'EUR'; // TODO!!!

	const form = useAppForm({
		validators: { onChange: detailsSchema(i18n, portalId) },
		validationLogic: revalidateLogic({
			mode: 'submit',
			modeAfterSubmission: 'change',
		}),
		defaultValues: portalId === 'telefonica' ? { ...defaultValues, certificate: true } : defaultValues,
		listeners: {
			onChangeDebounceMs: 300,
			onChange: ({ formApi }) => {
				setDetails(formApi.state.values);
			},
		},
		onSubmit: async ({ value }) => {
			setDetails(value);

			// Trigger preregistration and payment methods fetching
			await Promise.all([
				generateCaptchaToken().then((captchaToken) => {
					if (!amount) return;

					return preregisterDonation({
						certificated: value.certificate,
						amount: amount,
						campaignId,
						currency: currencyCode,
						applicationId: {
							type: applicationType.portal,
							host: portalId,
						},
						donor: {
							name: value.firstname,
							lastName: value.lastname,
							contact: {
								email: value.email,
								language: localeToLanguage[language],
								physicalAddress: {
									region: value.region,
									street: {
										name: value.streetName,
										number: value.streetNumber,
										type: '',
									},
									additionalInfo: value.additionalInfo,
									city: value.city,
									country: value.country,
									postalCode: value.cp,
									province: value.province,
								},
							},
							document: {
								number: value.documentNumber,
								type: value.documentType,
							},
							subscribeNewsletter: false,
						},
						checkbox: [
							termsContent && { type: 'termsAndConditions' as const, ...termsContent },
							ngosTermsContent && { type: 'ngoTermsAndConditions' as const, ...ngosTermsContent },
							telefonicaTermsContent && {
								type: 'telefonicaTermsAndConditions' as const,
								...telefonicaTermsContent,
							},
						].filter((v) => v !== undefined),
						captchaToken,
					}).then((result) => {
						if (result?.captchaPassed) {
							setCachedPreregistration(result);
						}
					});
				}),
				generateCaptchaToken().then((captchaToken) => {
					if (!amount) return;

					return getPaymentMethods({
						data: {
							amount: amountToMinorUnits(amount, currencyCode),
							countryCode: value.country,
							currencyCode,
							locale: language,
							captchaToken,
						},
					}).then((paymentMethods) => {
						if (paymentMethods.captchaPassed) {
							setCachedPaymentMethods(paymentMethods);
						}
					});
				}),
			]).catch((error) => {
				console.error('Background preregistration failed:', error);
				toast.error(i18n.errors.internal);
			});

			await navigate({ to: `/$portalId/$campaignId/donate/checkout`, params: { portalId, campaignId } });
		},
	});

	useEffect(() => {
		if (hasHydrated && !amount) navigate({ to: `/$portalId/$campaignId/donate`, params: { portalId, campaignId } });
	}, [amount, navigate, campaignId, hasHydrated, portalId]);

	const campaign = usePortalCampaignQuery(campaignId);
	const author = usePortalAuthorQuery();
	const setTermsContent = usePersistedStore((state) => state.setTermsContent);
	const setNgosTermsContent = usePersistedStore((state) => state.setNgosTermsContent);
	const setTelefonicaTermsContent = usePersistedStore((state) => state.setTelefonicaTermsContent);

	const [openTermsDialog, setOpenTermsDialog] = useState(false);
	const [openNgosTermsDialog, setOpenNgosTermsDialog] = useState(false);
	const [openTelefonicaTermsDialog, setOpenTelefonicaTermsDialog] = useState(false);

	const termsLabelRef = useRef<HTMLDivElement>(null);
	const termsDialogRef = useRef<HTMLDivElement>(null);
	const termsTextRef = useRef<HTMLTableElement>(null);
	const hiddenTermsTextRef = useRef<HTMLTableElement>(null);
	const ngosTermsLabelRef = useRef<HTMLDivElement>(null);
	const ngosTermsDialogRef = useRef<HTMLDivElement>(null);
	const ngosTermsTextRef = useRef<HTMLTableElement>(null);
	const hiddenNgosTermsTextRef = useRef<HTMLTableElement>(null);
	const telefonicaTermsLabelRef = useRef<HTMLDivElement>(null);
	const telefonicaTermsDialogRef = useRef<HTMLDivElement>(null);
	const telefonicaTermsTextRef = useRef<HTMLDivElement>(null);
	const hiddenTelefonicaTermsTextRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (hasHydrated && !amount) navigate({ to: `/$portalId/$campaignId/donate`, params: { campaignId } });
	}, [amount, navigate, campaignId, hasHydrated]);

	const documentTypeOptions = Object.entries(i18n.documentTypes).map(([value, label]) => ({ value, label }));

	if (isNotFound(campaign) || isNotFound(author)) return null;

	const ngoNames = formatList(
		language,
		campaign.data.ngos.map((ngo) => ngo.name ?? ''),
	);

	return (
		<Animate>
			<form
				onSubmit={(event) => {
					event.preventDefault();
					form.handleSubmit();
				}}
				className="flex flex-col gap-6"
			>
				<div className="flex flex-col gap-2">
					<Title size="lg" className="text-center">
						{i18n.donate.details.title}
					</Title>
					<Paragraph className="text-center">{i18n.donate.details.subtitle}</Paragraph>
				</div>
				<div className="flex flex-col gap-3">
					<div className="flex flex-col gap-3 sm:flex-row">
						<form.AppField
							name="firstname"
							children={(field) => <field.TextField label={i18n.labels.name} variant="outline" className="w-full" />}
						/>
						<form.AppField
							name="lastname"
							children={(field) => (
								<field.TextField label={i18n.labels.lastName} variant="outline" className="w-full" />
							)}
						/>
					</div>
					<form.AppField
						name="email"
						children={(field) => <field.TextField label={i18n.labels.email} variant="outline" />}
					/>
					<form.AppField
						name="repeatEmail"
						children={(field) => <field.TextField label={i18n.labels.repeatEmail} variant="outline" />}
					/>
				</div>
				<form.AppField
					name="certificate"
					children={(field) => (
						<label
							className={cn(
								'bg-primary/10 flex items-center gap-3 rounded-[12px] border border-transparent px-4 py-3 transition-colors',
								field.state.value && 'border-primary shadow-primary/20',
								// Don't show certificate option if not available for the merchant (in this case all italian portals)
								language === 'it' && 'hidden',
							)}
							style={{
								boxShadow: field.state.value
									? `0 0 12px 0px color-mix(in oklab, color-mix(in oklab, var(--primary) 20%, transparent) var(--tw-shadow-alpha), transparent)`
									: undefined,
							}}
							htmlFor={field.name}
						>
							<div className="bg-background hidden rounded-sm p-2 lg:block">
								<BeneficiariesIcon className="text-primary shrink-0" />
							</div>

							<div className="font-light">
								<div className="flex items-center gap-2">
									<div
										dangerouslySetInnerHTML={{
											__html: sanitizeHTML(
												i18n.donate.certificate.title.replace(
													'{{amount}}',
													`<b class="font-semibold">${formatNumber(language, savedTaxAmount(amount!))} €</b>`,
												),
											),
										}}
									/>
									{/* <Tooltip
										content={
											<>
												Según{' '}
												<a
													href="https://www.boe.es/buscar/act.php?id=BOE-A-2023-25758&p=20250103&tn=1#a1-41"
													target='_blank'
													rel='noopener noreferrer'
													className='underline'
												>
													Real Decreto-ley 6/2023 de 19 de diciembre
												</a>
											</>
										}
									>
										<InfoIcon className="-mt-[2px] inline size-4" />
									</Tooltip> */}
								</div>
								<p className="text-sm">{i18n.donate.certificate.subtitle}</p>
							</div>

							<field.SwitchField size="lg" variant="primary" className="text-foreground mx-auto w-fit" />
						</label>
					)}
				/>
				<form.Subscribe
					selector={(state) => state.values.certificate}
					children={(certificate) => (
						<div className={cn('flex flex-col gap-3', !certificate && portalId !== 'telefonica' && 'hidden')}>
							<div className="flex flex-col gap-3 sm:flex-row">
								<form.AppField
									name="documentType"
									children={(field) => (
										<field.SelectTextField
											label={i18n.labels.documentType}
											options={documentTypeOptions}
											variant="outline"
											className="w-full"
										/>
									)}
								/>
								<form.AppField
									name="documentNumber"
									children={(field) => (
										<field.TextField label={i18n.labels.documentNumber} variant="outline" className="w-full" />
									)}
								/>
							</div>
							<form.AppField
								name="streetName"
								children={(field) => <field.TextField label={i18n.labels.streetName} variant="outline" />}
							/>
							<form.AppField
								name="streetNumber"
								children={(field) => <field.TextField label={i18n.labels.streetNumber} variant="outline" />}
							/>
							<form.AppField
								name="additionalInfo"
								children={(field) => <field.TextField label={i18n.labels.additionalInfo} variant="outline" />}
							/>
							<form.AppField
								name="city"
								children={(field) => <field.TextField label={i18n.labels.city} variant="outline" />}
							/>
							<div className="flex flex-col gap-3 sm:flex-row">
								<form.AppField
									name="cp"
									children={(field) => <field.TextField label={i18n.labels.postalCode} variant="outline" />}
								/>
								<form.AppField
									name="country"
									children={(field) => (
										<field.CountryField label={i18n.labels.country} locale={language} variant="outline" />
									)}
								/>
							</div>
							<ClientOnly>
								<form.AppField
									name="region"
									validators={{ onChangeListenTo: ['country'] }}
									listeners={{
										onChange: () =>
											form.setFieldValue('province', '', {
												dontValidate: true,
												dontUpdateMeta: true,
												dontRunListeners: true,
											}),
									}}
									children={(field) => (
										<field.RegionField
											label={i18n.labels.region}
											country={form.getFieldValue('country')}
											variant="outline"
										/>
									)}
								/>
								<form.AppField
									name="province"
									validators={{ onChangeListenTo: ['country', 'region'] }}
									children={(field) => (
										<field.ProvinceField
											label={i18n.labels.province}
											country={form.getFieldValue('country')}
											region={form.getFieldValue('region')}
											variant="outline"
										/>
									)}
								/>
							</ClientOnly>
						</div>
					)}
				/>
				<div className="flex flex-col gap-5">
					<form.AppField
						name="termsAndConditions"
						listeners={{
							onChange: ({ value }) => {
								// When checkbox is toggled directly, capture text and label
								const currentContent = usePersistedStore.getState().termsContent;

								// If text and label are already set (e.g., from dialog accept), don't overwrite
								if (value && currentContent?.text && currentContent?.label) {
									return;
								}

								const textRef = termsTextRef.current || hiddenTermsTextRef.current;
								if (value && termsLabelRef.current && textRef) {
									setTermsContent({
										checked: true,
										label: termsLabelRef.current.innerHTML,
										text: textRef.innerHTML,
									});
								} else if (!value) {
									setTermsContent(undefined);
								}
							},
						}}
						children={(field) => (
							<TermsCheckboxField
								field={field}
								labelText={i18n.termsAndConditions.labelText}
								linkText={i18n.termsAndConditions.labelLink.replace('{{name}}', 'Worldcoo')}
								dialogTitle={i18n.termsAndConditions.title}
								acceptButtonText={i18n.termsAndConditions.accept}
								open={openTermsDialog}
								onOpenChange={setOpenTermsDialog}
								labelRef={termsLabelRef}
								dialogRef={termsDialogRef}
								textRef={termsTextRef}
								termsContent={
									<TermsAndConditionsDialog
										ref={termsTextRef}
										responsible={i18n.termsAndConditions.responsible.description}
										purpose={i18n.termsAndConditions.purpose.description}
										legitimacy={i18n.termsAndConditions.legitimacy.description}
										recipients={i18n.termsAndConditions.recipients.description}
										rights={i18n.termsAndConditions.rights.description}
										additionalInfo={i18n.termsAndConditions.additionalInfo.description}
									/>
								}
								onAccept={() => {
									if (!termsTextRef.current || !termsLabelRef.current) return;

									form.setFieldValue('termsAndConditions', true);
									setTermsContent({
										checked: true,
										label: termsLabelRef.current.innerHTML,
										text: termsTextRef.current.innerHTML,
									});
									setOpenTermsDialog(false);
								}}
							/>
						)}
					/>

					<form.AppField
						validators={{ onChangeListenTo: ['certificate'] }}
						name="ngosTermsAndConditions"
						listeners={{
							onChange: ({ value }) => {
								// When checkbox is toggled directly, capture text and label
								const currentContent = usePersistedStore.getState().ngosTermsContent;

								// If text and label are already set (e.g., from dialog accept), don't overwrite
								if (value && currentContent?.text && currentContent?.label) {
									return;
								}

								const textRef = ngosTermsTextRef.current || hiddenNgosTermsTextRef.current;
								if (value && ngosTermsLabelRef.current && textRef) {
									setNgosTermsContent({
										checked: true,
										label: ngosTermsLabelRef.current.innerHTML,
										text: textRef.innerHTML,
									});
								} else if (!value) {
									setNgosTermsContent(undefined);
								}
							},
						}}
						children={(field) => (
							<form.Subscribe
								selector={(state) => state.values.certificate}
								children={(certificate) => (
									<div className={cn(!certificate && 'hidden')}>
										<TermsCheckboxField
											field={field}
											labelText={i18n.termsAndConditions.labelText}
											linkText={i18n.termsAndConditions.labelLink.replace('{{name}}', ngoNames)}
											dialogTitle={i18n.termsAndConditions.title}
											acceptButtonText={i18n.termsAndConditions.accept}
											open={openNgosTermsDialog}
											onOpenChange={setOpenNgosTermsDialog}
											labelRef={ngosTermsLabelRef}
											dialogRef={ngosTermsDialogRef}
											textRef={ngosTermsTextRef}
											termsContent={
												<TermsAndConditionsDialog
													ref={ngosTermsTextRef}
													responsible={ngoNames}
													purpose={i18n.termsAndConditions.purpose.descriptionNgo}
													legitimacy={i18n.termsAndConditions.legitimacy.description}
													recipients={i18n.termsAndConditions.recipients.description}
													rights={i18n.termsAndConditions.rights.description}
													additionalInfo={i18n.termsAndConditions.additionalInfo.descriptionNgo.replace(
														'{{ngoName}}',
														ngoNames,
													)}
												/>
											}
											onAccept={() => {
												if (!ngosTermsTextRef.current || !ngosTermsLabelRef.current) return;

												form.setFieldValue('ngosTermsAndConditions', true);
												setNgosTermsContent({
													checked: true,
													label: ngosTermsLabelRef.current.innerHTML,
													text: ngosTermsTextRef.current.innerHTML,
												});
												setOpenNgosTermsDialog(false);
											}}
										/>
									</div>
								)}
							/>
						)}
					/>

					{portalId === 'telefonica' && (
						<form.AppField
							name="telefonicaTermsAndConditions"
							listeners={{
								onChange: ({ value }) => {
									// When checkbox is toggled directly, capture text and label
									const currentContent = usePersistedStore.getState().telefonicaTermsContent;

									// If text and label are already set (e.g., from dialog accept), don't overwrite
									if (value && currentContent?.text && currentContent?.label) {
										return;
									}

									const textRef = telefonicaTermsTextRef.current || hiddenTelefonicaTermsTextRef.current;
									if (value && telefonicaTermsLabelRef.current && textRef) {
										setTelefonicaTermsContent({
											checked: true,
											label: telefonicaTermsLabelRef.current.innerHTML,
											text: textRef.innerHTML,
										});
									} else if (!value) {
										setTelefonicaTermsContent(undefined);
									}
								},
							}}
							children={(field) => (
								<TermsCheckboxField
									field={field}
									labelText={i18n.termsAndConditions.labelText}
									linkText={i18n.termsAndConditions.labelLink.replace('{{name}}', author.name ?? '')}
									dialogTitle={i18n.termsAndConditions.title}
									acceptButtonText={i18n.termsAndConditions.accept}
									open={openTelefonicaTermsDialog}
									onOpenChange={setOpenTelefonicaTermsDialog}
									labelRef={telefonicaTermsLabelRef}
									dialogRef={telefonicaTermsDialogRef}
									textRef={telefonicaTermsTextRef}
									hideUnresetTailwind
									termsContent={<TelefonicaTermsAndConditions ref={telefonicaTermsTextRef} />}
									onAccept={() => {
										if (!telefonicaTermsTextRef.current || !telefonicaTermsLabelRef.current) return;

										form.setFieldValue('telefonicaTermsAndConditions', true);
										setTelefonicaTermsContent({
											checked: true,
											label: telefonicaTermsLabelRef.current.innerHTML,
											text: telefonicaTermsTextRef.current.innerHTML,
										});
										setOpenTelefonicaTermsDialog(false);
									}}
								/>
							)}
						/>
					)}
				</div>
				{/* Hidden terms content for capturing refs when checkboxes are toggled directly */}
				<div className="hidden">
					<TermsAndConditionsDialog
						ref={hiddenTermsTextRef}
						responsible={i18n.termsAndConditions.responsible.description}
						purpose={i18n.termsAndConditions.purpose.description}
						legitimacy={i18n.termsAndConditions.legitimacy.description}
						recipients={i18n.termsAndConditions.recipients.description}
						rights={i18n.termsAndConditions.rights.description}
						additionalInfo={i18n.termsAndConditions.additionalInfo.description}
					/>
					<form.Subscribe
						selector={(state) => state.values.certificate}
						children={(certificate) =>
							certificate ? (
								<TermsAndConditionsDialog
									ref={hiddenNgosTermsTextRef}
									responsible={ngoNames}
									purpose={i18n.termsAndConditions.purpose.descriptionNgo}
									legitimacy={i18n.termsAndConditions.legitimacy.description}
									recipients={i18n.termsAndConditions.recipients.description}
									rights={i18n.termsAndConditions.rights.description}
									additionalInfo={i18n.termsAndConditions.additionalInfo.descriptionNgo.replace(
										'{{ngoName}}',
										ngoNames,
									)}
								/>
							) : null
						}
					/>
					{portalId === 'telefonica' && <TelefonicaTermsAndConditions ref={hiddenTelefonicaTermsTextRef} />}
				</div>
				<form.AppForm>
					<form.SubmitButton size="2xl" className="mx-auto mt-6 w-full min-w-fit lg:w-60" label={i18n.actions.next} />
				</form.AppForm>
			</form>
		</Animate>
	);
}

function TelefonicaTermsAndConditions(props: { ref: RefObject<HTMLDivElement | null> }) {
	return (
		<div
			ref={props.ref}
			className="unreset-tailwind"
			dangerouslySetInnerHTML={{
				__html:
					'<p><strong>AVISO LEGAL PARA LA TOMA DE DATOS DE CARÁCTER PERSONAL</strong></p><p>FUNDACIÓN TELEFÓNICA respeta la legislación vigente en materia de protección de datos personales, la privacidad de los usuarios y el secreto y seguridad de los datos personales, en concreto el Reglamento 2016/679 del Parlamento Europeo y del Consejo de 27 de abril de 2016, adoptando para ello las medidas técnicas y organizativas necesarias para evitar la pérdida, mal uso, alteración, acceso no autorizado y robo de los datos personales facilitados, habida cuenta del estado de la tecnología, la naturaleza de los datos y los riesgos a los que están expuestos.</p><p>Al efectuar tu donativo, los datos que facilites (nombre y apellidos, correo electrónico) serán tratados por Fundación Telefónica, con la finalidad de gestionar tu participación en esta iniciativa y, en su caso, enviarte comunicaciones de tu interés en relación al mismo y al Programa Voluntarios Telefónica. Tus datos podrán ser cedidos a terceros, para coordinar tu participación en la actividad. En el caso de ser empleado de una empresa del grupo Telefónica, Fundación Telefónica o Fundación ProFuturo, tus datos podrán ser cedidos a la correspondiente área de Personas de dichas entidades, para coordinar y gestionar tu participación en esta actividad de voluntariado corporativo.</p><p>&nbsp;</p><p>De acuerdo con la actual legislación, queda informado y acepta el tratamiento de sus datos con la finalidad descrita a continuación:</p><p>&nbsp;</p><p><strong>Información sobre protección de datos</strong></p><p>Responsable:&nbsp;Fundación Telefónica.</p><p>Finalidad del Tratamiento:&nbsp;Recogida de los datos de donantes que contribuyen a causas promovidas por Fundación Telefónica. Como entidad sin ánimo de lucro y dentro del desarrollo de sus fines fundacionales, Fundación Telefónica le mantendrá informado de su actividad.</p><p>Legitimación:&nbsp;Consentimiento del interesado y obligación legal</p><p>Cesiones:&nbsp;Administración tributaria.</p><p>Derechos:&nbsp;A retirar su consentimiento en cualquier momento, a oponerse al tratamiento, a acceder, rectificar y suprimir los datos, así como otros derechos, tal y como se explica en la información adicional.</p><p>Transferencias Internacionales:&nbsp;No se realizan transferencias internacionales de datos de carácter personal.</p><p>Información Adicional:&nbsp;<a href="https://legal.fundaciontelefonica.com/informacion-adicional-proteccion-de-datos/" target="_blank" rel="noopener noreferrer" class="underline">https://legal.fundaciontelefonica.com/informacion-adicional-proteccion-de-datos/</a></p>',
			}}
		/>
	);
}
